import { LoaderCircle } from 'lucide-react';
import React from 'react';

import { classnames } from '@/utils/classnames.ts';

import Slot, { type Slottable } from './Slot.tsx';

export interface ButtonProps extends React.ComponentPropsWithRef<'button'>, Slottable {
    variant?: 'primary' | 'secondary' | 'muted' | 'ghost' | 'danger';
    size?: 'sm' | 'md';
    // While its action runs: a spinner before the label, and disabled. Not for `asChild`, whose child is the content.
    isPending?: boolean;
    // Less room at its sides, for a short label among others, e.g. a count or "Zurück".
    isCompact?: boolean;
}

// Styles both native buttons and react-aria elements (via asChild), so every state is matched by pseudo-class and data attribute.
const Button = ({
    asChild,
    variant = 'secondary',
    size = 'md',
    isPending = false,
    isCompact = false,
    disabled,
    className,
    children,
    ...props
}: ButtonProps) => {
    const Comp = asChild ? Slot : 'button';

    return (
        <Comp
            type={asChild ? undefined : 'button'}
            disabled={disabled || isPending}
            // For what lines it up by its text rather than its edge, e.g. a section header (Page.SectionHeader).
            data-variant={variant}
            {...props}
            className={classnames(
                'inline-flex shrink-0 cursor-default items-center justify-center gap-2 rounded-full font-medium whitespace-nowrap outline-none transition-colors select-none',
                'focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface data-[focus-visible]:ring-2 data-[focus-visible]:ring-primary data-[focus-visible]:ring-offset-2 data-[focus-visible]:ring-offset-surface',
                'disabled:opacity-50 data-[disabled]:opacity-50',
                size === 'md' && ['h-10 text-base', isCompact ? 'px-3' : 'px-5'],
                size === 'sm' && ['h-8 text-sm', isCompact ? 'px-2' : 'px-3.5'],
                variant === 'primary' && 'bg-primary text-on-primary hovered:bg-primary-hover',
                variant === 'secondary' && 'border border-border bg-surface text-text hovered:bg-surface-muted',
                variant === 'muted' && 'bg-surface-muted text-text hovered:bg-surface-hover',
                variant === 'danger' && 'bg-negative text-white hovered:opacity-90',
                variant === 'ghost' && 'text-text-muted hovered:bg-surface-muted hovered:text-text',
                className
            )}
        >
            {isPending && !asChild ? (
                <>
                    <LoaderCircle className="animate-spin" />
                    {children}
                </>
            ) : (
                children
            )}
        </Comp>
    );
};

export default Button;
